import { makeStyles } from "@material-ui/core"
import { FC } from "react"
import StatTile from "../atoms/StatTile"
import { VirasafeMetrics } from "../../utils/types"

/**
 * MoodTrendsActivityStatRow is a functional component that displays a row of statistical tiles
 * related to mood trends and activity insights. Each tile represents a specific metric, such as
 * day streaks, weekly "Keep Me Safe" activities, and weekly "Keep Me Calm" activities.
 *
 * @component
 * @param {Object} props - The props object.
 * @param {Object} props.insights - Insights data containing metrics and activity counts.
 * @param {RealmVirasafeMetrics | null} props.insights.metrics - Metrics data from RealmVirasafe, including streaks and weekly activity counts.
 * @param {number | null} props.insights.numTopEngagedActivities - Number of top engaged activities (unused in this component).
 * @param {number | null} props.insights.numLeastDistressedActivities - Number of least distressed activities (unused in this component).
 *
 * @returns {JSX.Element} A row of statistical tiles displaying mood trends and activity metrics.
 *
 * @remarks
 * This component uses Recoil for state management to retrieve the current theme and applies
 * dynamic styling based on the theme.
 *
 * @example
 * ```tsx
 * <MoodTrendsActivityStatRow
 *   insights={{
 *     metrics: {
 *       n_day_streak: 5,
 *       n_weekly_keep_me_safe: 3,
 *       n_weekly_keep_me_calm: 2,
 *     },
 *     numTopEngagedActivities: null,
 *     numLeastDistressedActivities: null,
 *   }}
 * />
 * ```
 */
export const MoodTrendsActivityStatRow: FC<{
  insights: {
    metrics: VirasafeMetrics | null
    numTopEngagedActivities: number | null
    numLeastDistressedActivities: number | null
  }
}> = ({ insights }) => {
  const styles = useStyles()
  return (
    <div className={styles.container}>
      <StatTile
        topText="Activities"
        value={insights.metrics?.n_day_streak}
        bottomText="Day Streak"
        width={"33%"}
      />
      <div className={styles.spacer} />
      <StatTile
        topText="Keep Me Safe"
        value={insights.metrics?.n_weekly_keep_me_safe}
        bottomText="Times This Week"
        width={"33%"}
      />
      <div className={styles.spacer} />
      <StatTile
        topText="Keep Me Calm"
        value={insights.metrics?.n_weekly_keep_me_calm}
        bottomText="Times This Week"
        width={"33%"}
      />
    </div>
  )
}

const useStyles = makeStyles(() => ({
  container: {
    display: "flex",
    width: "100%",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  spacer: {
    width: 16,
  },
}))
